import { z } from 'zod';
import type { VoyageCharter_OrderFragment } from '../../graphql';
import {
  CargoDeckOptionType,
  ImoClass,
  LoadingUnit,
  MarginUnit,
  useUpdateOrderMutation,
} from '../../graphql';
import {
  useCreateOrderWithRelationsMutation,
  useUpdateOrderWithRelationsMutation,
} from '../order/order';
import type { VoyageCharterSchema } from './schema';
import { valueAndLabel } from '../order/OrderForm';
import { CargoExclusionType } from '../order/cargoExclusion';
import type { ScenarioOrder } from '../../lib/planningData';

export const assortmentOptionsSchema = z.object({
  stowageFactor: z
    .object({
      min: z.number().nullable(),
      max: z.number().nullable(),
    })
    .optional(),
  imoClass: z.nativeEnum(ImoClass).nullable(),
  specificGravity: z.number().nullable().optional(),
  marginAmount: z.number().nullable(),
  marginUnit: z.nativeEnum(MarginUnit).nullable(),
  cargoExclusions: z.array(valueAndLabel).nullable(),
  cargoExclusionAmount: z.number().optional().nullable(),
  cargoExclusionType: z.nativeEnum(CargoExclusionType).nullable(),
});

export const voyageCharterOrderSchema = z.object({
  id: z.number().or(z.string()),
  assortmentId: z.number(),
  cargoAmount: z.number(),
  cargoUnitId: z.number().nullable(),
  loadPortId: z.number(),
  dischargePortId: z.number(),
  extraInfo: z.string().nullable(),
  orderName: z.string().nullable(),
  fixtureNote: z.string().nullable(),
  cargoDeckOption: z.nativeEnum(CargoDeckOptionType).nullable(),
  externalOrderId: z.string().nullable(),
  loadingRate: z.number().optional().nullable(),
  loadingUnit: z.nativeEnum(LoadingUnit).optional().nullable(),
  layday: z.date().nullable(),
  cancelling: z.date().nullable(),
  rta: z.date().nullable(),
  deliveredBefore: z.date().nullable(),
  loadingOverheadDuration: z.number().optional().nullable(),
  dischargingOverheadDuration: z.number().optional().nullable(),
  dischargingRate: z.number().optional().nullable(),
  dischargingUnit: z.nativeEnum(LoadingUnit).optional().nullable(),
  assortmentOptions: assortmentOptionsSchema.nullable(),
});

export type VoyageCharterOrderSchema = z.infer<typeof voyageCharterOrderSchema>;

export type OrderWithVoyageCharterValues = Omit<
  VoyageCharterOrderSchema,
  'layday' | 'cancelling' | 'rta' | 'deliveredBefore'
> & {
  coaId: number | null;
  chartererOrganizationId: number | null;
  layday?: string | null;
  cancelling?: string | null;
  rta?: string | null;
  deliveredBefore?: string | null;
};

export function useVoyageCharterOrdersSave(
  currentOrders: VoyageCharter_OrderFragment[],
) {
  const [createOrder] = useCreateOrderWithRelationsMutation();
  const [updateOrder] = useUpdateOrderWithRelationsMutation();
  const [softDeleteOrder] = useUpdateOrderMutation();

  /** @deprecated using useVcAndOrdersMutation instead */
  const save = async (newOrders: OrderWithVoyageCharterValues[]) => {
    const createdOrderIdByTmpId = new Map<string, number>();

    const newOrderIds = new Set(newOrders.map((o) => o.id));

    const createdOrders = newOrders.filter(
      (o): o is typeof o & { id: string } => typeof o.id === 'string',
    );
    const deletedOrders = currentOrders.filter((o) => !newOrderIds.has(o.id));
    const updatedOrders = newOrders.filter(
      (o): o is typeof o & { id: number } => typeof o.id === 'number',
    );

    for (const { id, extraInfo, ...order } of createdOrders) {
      const responses = await createOrder(order, { extraInfo }, [], [], []);

      const createdId = responses.data?.[0].createOrder?.order?.id ?? null;

      if (createdId) {
        createdOrderIdByTmpId.set(id, createdId);
      }
    }

    for (const { extraInfo, ...order } of updatedOrders) {
      const orderDetailsId =
        currentOrders.find((currentOrder) => currentOrder.id === order.id)
          ?.orderDetailsByOrderId.nodes[0]?.id ?? null;

      await updateOrder(
        order,
        { id: orderDetailsId, extraInfo },
        [],
        [],
        [],
        [],
        [],
        [],
      );
    }

    for (const order of deletedOrders) {
      await softDeleteOrder({
        variables: {
          input: {
            id: order.id,
            orderPatch: {
              softDelete: true,
            },
          },
        },
      });
    }

    return {
      createdOrderIdByTmpId,
    };
  };

  const remove = async () => {
    for (const order of currentOrders) {
      await softDeleteOrder({
        variables: {
          input: {
            id: order.id,
            orderPatch: {
              softDelete: true,
            },
          },
        },
        update(cache) {
          cache.evict({ id: cache.identify(order) });
          cache.gc();
        },
      });
    }
  };

  return { remove, save };
}

type ObjectWithLaycan = {
  layday?: string | null;
  cancelling?: string | null;
};

type OrderWithDates = {
  id: string | number;
  layday: Date | null;
  cancelling: Date | null;
};

/**
 * Determines which orders should have laycan override based on their relationship with voyage charter values
 *
 * @param currentOrders - Existing orders with their current laycan values
 * @param currentVoyageCharter - Current voyage charter with its laycan values
 * @param newOrders - Updated orders with new laycan values
 * @param newVoyageCharter - Updated voyage charter with new laycan values
 * @returns Array of order IDs that should have laycan override
 *
 * Orders are marked for override when:
 * - Existing order with values matching voyage charter has values manually changed
 * - Existing order with override has different values from new voyage charter
 * - New order has values explicitly set different from voyage charter
 * - Order transitioning from null values has values manually set
 */
export function getOrdersWithLaycanOverride(
  currentOrders: (ObjectWithLaycan & { id: number })[],
  currentVoyageCharter: ObjectWithLaycan | null,
  newOrders: OrderWithDates[],
  newVoyageCharter: ObjectWithLaycan,
): (string | number)[] {
  return newOrders
    .filter((order) => {
      /* Convert all dates to milliseconds to ensure consistent comparison */
      const currentVcLaydayValue = currentVoyageCharter?.layday
        ? new Date(currentVoyageCharter.layday).valueOf()
        : null;
      const currentVcCancellingValue = currentVoyageCharter?.cancelling
        ? new Date(currentVoyageCharter.cancelling).valueOf()
        : null;

      const newVcLaydayValue = newVoyageCharter.layday
        ? new Date(newVoyageCharter.layday).valueOf()
        : null;
      const newVcCancellingValue = newVoyageCharter.cancelling
        ? new Date(newVoyageCharter.cancelling).valueOf()
        : null;

      const orderLaydayValue = order.layday?.valueOf() ?? null;
      const orderCancellingValue = order.cancelling?.valueOf() ?? null;

      const existingOrder =
        typeof order.id === 'number'
          ? currentOrders.find((o) => o.id === order.id)
          : null;

      if (!existingOrder) {
        /* Only mark for new orders overridden if values were explicitly set */
        return (
          (orderLaydayValue !== null || orderCancellingValue !== null) &&
          (orderLaydayValue !== newVcLaydayValue ||
            orderCancellingValue !== newVcCancellingValue)
        );
      }

      /* Special case: transitioning from null values */
      const ordersHadNullValues =
        existingOrder.layday === null && existingOrder.cancelling === null;

      if (ordersHadNullValues) {
        /* Only mark for override if order values changed from null */
        return orderLaydayValue !== null || orderCancellingValue !== null;
      }

      /* For existing orders, check if they were following VC */
      const wasFollowingVc =
        (existingOrder.layday === currentVoyageCharter?.layday ||
          (!existingOrder.layday && !currentVoyageCharter?.layday)) &&
        (existingOrder.cancelling === currentVoyageCharter?.cancelling ||
          (!existingOrder.cancelling && !currentVoyageCharter?.cancelling));

      if (wasFollowingVc) {
        /* Check if order values were manually changed from old VC values */
        const wasManuallyChanged =
          orderLaydayValue !== currentVcLaydayValue ||
          orderCancellingValue !== currentVcCancellingValue;

        return wasManuallyChanged;
      }

      /* If not following VC, only remove override if explicitly matching new VC */
      return !(
        orderLaydayValue === newVcLaydayValue &&
        orderCancellingValue === newVcCancellingValue
      );
    })
    .map((order) => order.id);
}

export function applyVoyageCharterValuesToOrders(
  voyageCharter: VoyageCharterSchema,
  orders: VoyageCharterOrderSchema[],
  /** Orders that should not receive laycan values from the voyage charter  */
  ordersWithLaycanOverride: (number | string)[],
): OrderWithVoyageCharterValues[] {
  return orders.map((order) => {
    const hasLaycanOverride = ordersWithLaycanOverride.includes(order.id);

    const coaId =
      voyageCharter.contractDetails.type === 'coa'
        ? voyageCharter.contractDetails.coaId
        : null;

    const orderLayday = order.layday ? order.layday.toISOString() : null;
    const orderCancelling = order.cancelling
      ? order.cancelling.toISOString()
      : null;
    const orderRta = order.rta ? order.rta.toISOString() : null;
    const orderDeliveredBefore = order.deliveredBefore
      ? order.deliveredBefore.toISOString()
      : null;

    const layday = hasLaycanOverride
      ? orderLayday
      : voyageCharter.baseContract.layday;
    const cancelling = hasLaycanOverride
      ? orderCancelling
      : voyageCharter.baseContract.cancelling;

    return {
      ...order,
      coaId,
      chartererOrganizationId: voyageCharter.baseContract.chartererOrgId,
      loadingRate: order?.loadingRate ?? null,
      loadingUnit: order?.loadingUnit ?? null,
      dischargingRate: order?.dischargingRate ?? null,
      dischargingUnit: order?.dischargingUnit ?? null,
      layday,
      cancelling,
      rta: orderRta,
      deliveredBefore: orderDeliveredBefore,
    };
  });
}

export function isInquiry(order: ScenarioOrder): boolean {
  const incomingVc =
    order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId;

  return !incomingVc || incomingVc.isInquiry;
}
